<template>
	<div class="tables-analytics-container">

		<SummaryCardBest
			ref="initial-card"
			:cards-info="todayCardsInfo"
			:data="bestTableTodayOrders.tablesSeries"
			data-field-value="totalFormatted" />

		<FiltersRow ref="filters" @changed-timeframe="changedTimeframe" @switched-chart="tables => showCharts = !tables" with-table-switch :loading="isLoading" />

		<TablesChartsAggregated :dates="periodDates" v-if="showCharts" />
		<TablesTablesAggregated :dates="periodDates" v-else />

	</div>
</template>

<script>
import {useAnalyticsStore} from "@/console-new/stores";
import {useUserStore} from "@/user/stores";
import {useLanguageStore} from "@/shared/stores";
import SummaryCardBest from "@/console-new/components/analytics/SummaryCardBest.vue";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import FiltersRow from "@/console-new/components/analytics/FiltersRow.vue";
import TablesChartsAggregated from "@/console-new/components/analytics/TablesChartsAggregated.vue";
import TablesTablesAggregated from "@/console-new/components/analytics/TablesTablesAggregated.vue";

export default {
	setup() {
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		return {analyticsStore, userStore, getString: languageStore.getString};
	},
	name: "TablesContainer",
	components: {SummaryCardBest, FiltersRow, TablesChartsAggregated, TablesTablesAggregated},
	data() {
		return {
			realtimeTimeout: null,
			periodDates: {
				end: this.analyticsStore.getTargetEndDate(),
				start: this.analyticsStore.getTargetStartDate(0),
				previousStart: this.analyticsStore.getTargetPreviousStartDate(0),
				previousEnd: this.analyticsStore.getTargetPreviousEndDate(0),
				timeframeIndex: 0,
			},
			showCharts: true,
			isLoading: false,
		};
	},
	computed: {
		filteredTodayOrders() {
			const now = new Date();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.tablesAggregatedToday.tables_orders || this.analyticsStore.tablesAggregatedToday.tables_orders.length === 0) {
				return [];
			}
			
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);
			
			const filteredData = this.analyticsStore.tablesAggregatedToday.tables_orders.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so 03:00 contains orders from 02:00-03:00
				// We want to exclude the shift hour itself to avoid counting pre-shift orders
				return date > todayShiftStart && date <= nowRounded;
			});
			
			// Create a lookup for table names
			const tableMetadataLookup = {};
			if (this.analyticsStore.tablesAggregatedToday.tables_metadata) {
				this.analyticsStore.tablesAggregatedToday.tables_metadata.forEach(table => {
					tableMetadataLookup[table.room_table_id] = table;
				});
			}
			
			// Return data in the format expected by bestTableTodayOrders
			return filteredData.map(row => {
				const metadata = tableMetadataLookup[row.room_table_id] || {};
				return {
					date: new Date(row.hour_bucket || row.day_bucket),
					room_table_id: row.room_table_id,
					totalFormatted: row.gross_sales / 100,
					count: row.orders_cnt || 0,
					cover_total: row.cover_total || 0,
					table: metadata.table_name ? { table_name: metadata.table_name } : null
				};
			});
		},
		filteredTodayPreviousOrders() {
			const now = new Date();
			const yesterdayShiftStart = this.analyticsStore.getPreviousDayShiftDatetime();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.tablesAggregatedToday.tables_orders || this.analyticsStore.tablesAggregatedToday.tables_orders.length === 0) {
				return [];
			}

			// Calculate time elapsed since today's shift start
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);

			const elapsedTime = nowRounded.getTime() - todayShiftStart.getTime();
			const yesterdayEndTime = new Date(yesterdayShiftStart.getTime() + elapsedTime);

			const filteredData = this.analyticsStore.tablesAggregatedToday.tables_orders.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so exclude the shift hour itself
				return date > yesterdayShiftStart && date <= yesterdayEndTime;
			});
			
			// Create a lookup for table names
			const tableMetadataLookup = {};
			if (this.analyticsStore.tablesAggregatedToday.tables_metadata) {
				this.analyticsStore.tablesAggregatedToday.tables_metadata.forEach(table => {
					tableMetadataLookup[table.room_table_id] = table;
				});
			}
			
			// Return data in the format expected by bestTableTodayOrders
			return filteredData.map(row => {
				const metadata = tableMetadataLookup[row.room_table_id] || {};
				return {
					date: new Date(row.hour_bucket || row.day_bucket),
					room_table_id: row.room_table_id,
					totalFormatted: row.gross_sales / 100,
					count: row.orders_cnt || 0,
					cover_total: row.cover_total || 0,
					table: metadata.table_name ? { table_name: metadata.table_name } : null
				};
			});
		},
		filteredTodayRoomOrders() {
			const now = new Date();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.tablesAggregatedToday.rooms_orders || this.analyticsStore.tablesAggregatedToday.rooms_orders.length === 0) {
				return [];
			}
			
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);
			
			const filteredData = this.analyticsStore.tablesAggregatedToday.rooms_orders.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so 03:00 contains orders from 02:00-03:00
				// We want to exclude the shift hour itself to avoid counting pre-shift orders
				return date > todayShiftStart && date <= nowRounded;
			});
			
			// Create a lookup for room names
			const roomMetadataLookup = {};
			if (this.analyticsStore.tablesAggregatedToday.rooms_metadata) {
				this.analyticsStore.tablesAggregatedToday.rooms_metadata.forEach(room => {
					roomMetadataLookup[room.room_id] = room;
				});
			}
			
			// Return data in the format expected by bestRoomTodayOrders
			return filteredData.map(row => {
				const metadata = roomMetadataLookup[row.room_id] || {};
				return {
					date: new Date(row.hour_bucket || row.day_bucket),
					room_id: row.room_id,
					totalFormatted: row.gross_sales / 100,
					count: row.orders_cnt || 0,
					cover_total: row.cover_total || 0,
					room: metadata.name ? { room_name: metadata.name } : null
				};
			});
		},
		filteredTodayPreviousRoomOrders() {
			const now = new Date();
			const yesterdayShiftStart = this.analyticsStore.getPreviousDayShiftDatetime();
			const todayShiftStart = this.analyticsStore.getDayShiftDatetime();
			
			if (!this.analyticsStore.tablesAggregatedToday.rooms_orders || this.analyticsStore.tablesAggregatedToday.rooms_orders.length === 0) {
				return [];
			}

			// Calculate time elapsed since today's shift start
			const nowRounded = new Date(now);
			if (nowRounded.getMinutes() > 0 || nowRounded.getSeconds() > 0) {
				nowRounded.setTime(nowRounded.getTime() + 3600 * 1000);
			}
			nowRounded.setMinutes(0, 0, 0);

			const elapsedTime = nowRounded.getTime() - todayShiftStart.getTime();
			const yesterdayEndTime = new Date(yesterdayShiftStart.getTime() + elapsedTime);

			const filteredData = this.analyticsStore.tablesAggregatedToday.rooms_orders.filter(row => {
				const date = new Date(row.hour_bucket || row.day_bucket);
				// hour_bucket represents the END of the hour, so exclude the shift hour itself
				return date > yesterdayShiftStart && date <= yesterdayEndTime;
			});
			
			// Create a lookup for room names
			const roomMetadataLookup = {};
			if (this.analyticsStore.tablesAggregatedToday.rooms_metadata) {
				this.analyticsStore.tablesAggregatedToday.rooms_metadata.forEach(room => {
					roomMetadataLookup[room.room_id] = room;
				});
			}
			
			// Return data in the format expected by bestRoomTodayOrders
			return filteredData.map(row => {
				const metadata = roomMetadataLookup[row.room_id] || {};
				return {
					date: new Date(row.hour_bucket || row.day_bucket),
					room_id: row.room_id,
					totalFormatted: row.gross_sales / 100,
					count: row.orders_cnt || 0,
					cover_total: row.cover_total || 0,
					room: metadata.name ? { room_name: metadata.name } : null
				};
			});
		},
		todayCardsInfo() {
			return [
				{
					title: this.bestTableTodayOrders.tablesName ? this.getString("best_product_param", [this.bestTableTodayOrders.tablesName]) : this.getString("best_product"),
					value: this.bestTableTodayOrders.revenues,
					previousValue: this.bestTableTodayOrders.revenuesPrevious,
				},
				{
					title: this.bestTableTodayOrders.tablesName ? this.getString("best_product_num_param", [this.bestTableTodayOrders.tablesName]) : this.getString("best_product_num"),
					value: this.bestTableTodayOrders.orders,
					previousValue: this.bestTableTodayOrders.ordersPrevious,
					noCurrency: true,
				},
				{
					title: this.bestRoomTodayOrders.roomsName ? this.getString("best_product_param", [this.bestRoomTodayOrders.roomsName]) : this.getString("best_rooms_revenue"),
					value: this.bestRoomTodayOrders.revenues,
					previousValue: this.bestRoomTodayOrders.revenuesPrevious,
				},
				{
					title: this.bestRoomTodayOrders.roomsName ? this.getString("best_product_num_param", [this.bestRoomTodayOrders.roomsName]) : this.getString("best_rooms_orders"),
					value: this.bestRoomTodayOrders.orders,
					previousValue: this.bestRoomTodayOrders.ordersPrevious,
					noCurrency: true,
				},
			];
		},
		bestTableTodayOrders() {
			let bestTableName = null;
			let maxRevenuesTable = null;
			let maxRevenuesTablePrevious = null;
			let maxOrdersTable = null;
			let maxOrdersTablePrevious = null;

			let tables = {};

			let activeCharts = {
				orders: false,
			};

			this.filteredTodayOrders.forEach(order => {

				if(order.room_table_id) {
					if (tables.hasOwnProperty(order.room_table_id)) {
						tables[order.room_table_id].data.push(order);
						tables[order.room_table_id].totalFormatted += order.totalFormatted;
						tables[order.room_table_id].count += order.count;
					} else {
						tables[order.room_table_id] = {
							name: order.table ? order.table.table_name : '-',
							room_table_id: order.room_table_id,
							data: [],
						}
						tables[order.room_table_id].data.push(order);
						tables[order.room_table_id].totalFormatted = order.totalFormatted;
						tables[order.room_table_id].count = order.count;
					}

					activeCharts.orders = true;
				}
			});

			if(activeCharts.orders) {
				tables = Object.values(tables);
				tables.sort((a, b) => {
					return b.totalFormatted - a.totalFormatted;
				});
				tables = tables.slice(0, 6);
				for(let i = 0; i < tables.length; i++) {
					tables[i].color = this.analyticsStore.bestColors[i];
				}
				let bestTable = tables[0];
				bestTableName = bestTable.name;
				maxRevenuesTable = bestTable.totalFormatted;
				maxOrdersTable = bestTable.count;
				
				// Store the best table for comparison with previous period
				if(bestTable) {
					this.filteredTodayPreviousOrders.forEach(order => {
						if(order.room_table_id === bestTable.room_table_id) {
							if (maxRevenuesTablePrevious === null) {
								maxRevenuesTablePrevious = order.totalFormatted;
							} else {
								maxRevenuesTablePrevious += order.totalFormatted;
							}
							if (maxOrdersTablePrevious === null) {
								maxOrdersTablePrevious = order.count;
							} else {
								maxOrdersTablePrevious += order.count;
							}
						}
					});
				}
			}

			return {
				tablesSeries: activeCharts.orders ? tables : [],
				tablesName: bestTableName,
				revenues: maxRevenuesTable,
				revenuesPrevious: maxRevenuesTablePrevious,
				orders: maxOrdersTable,
				ordersPrevious: maxOrdersTablePrevious,
			}
		},
		bestRoomTodayOrders() {
			let bestRoomName = null;
			let maxRevenuesRoom = null;
			let maxRevenuesRoomPrevious = null;
			let maxOrdersRoom = null;
			let maxOrdersRoomPrevious = null;

			let rooms = {};

			let activeCharts = {
				orders: false,
			};

			this.filteredTodayRoomOrders.forEach(order => {
				if(order.room_id) {
					if (rooms.hasOwnProperty(order.room_id)) {
						rooms[order.room_id].data.push(order);
						rooms[order.room_id].totalFormatted += order.totalFormatted;
						rooms[order.room_id].count += order.count;
					} else {
						rooms[order.room_id] = {
							name: order.room ? order.room.room_name : '-',
							room_id: order.room_id,
							data: [],
						}
						rooms[order.room_id].data.push(order);
						rooms[order.room_id].totalFormatted = order.totalFormatted;
						rooms[order.room_id].count = order.count;
					}

					activeCharts.orders = true;
				}
			});

			if(activeCharts.orders) {
				rooms = Object.values(rooms);
				rooms.sort((a, b) => {
					return b.totalFormatted - a.totalFormatted;
				});
				rooms = rooms.slice(0, 6);
				for(let i = 0; i < rooms.length; i++) {
					rooms[i].color = this.analyticsStore.bestColors[i];
				}
				let bestRoom = rooms[0];
				bestRoomName = bestRoom.name;
				maxRevenuesRoom = bestRoom.totalFormatted;
				maxOrdersRoom = bestRoom.count;
				
				// Store the best room for comparison with previous period
				if(bestRoom) {
					this.filteredTodayPreviousRoomOrders.forEach(order => {
						if(order.room_id === bestRoom.room_id) {
							if (maxRevenuesRoomPrevious === null) {
								maxRevenuesRoomPrevious = order.totalFormatted;
							} else {
								maxRevenuesRoomPrevious += order.totalFormatted;
							}
							if (maxOrdersRoomPrevious === null) {
								maxOrdersRoomPrevious = order.count;
							} else {
								maxOrdersRoomPrevious += order.count;
							}
						}
					});
				}
			}

			return {
				roomsSeries: activeCharts.orders ? rooms : [],
				roomsName: bestRoomName,
				revenues: maxRevenuesRoom,
				revenuesPrevious: maxRevenuesRoomPrevious,
				orders: maxOrdersRoom,
				ordersPrevious: maxOrdersRoomPrevious,
			}
		}
	},
	methods: {
		updateTodayTables(callback = null) {
			let now = new Date();
			let endDate = new Date(now);
			if (endDate.getMinutes() > 0 || endDate.getSeconds() > 0) {
				endDate.setTime(endDate.getTime() + 3600 * 1000);
			}
			endDate.setMinutes(0, 0, 0);

			// Fetch from yesterday's day shift to cover both today and yesterday's same period
			let startDate = this.analyticsStore.getPreviousDayShiftDatetime();
			let endDateDB = formatDateTimeWithSeconds(endDate);
			let startDateDB = formatDateTimeWithSeconds(startDate);

			this.analyticsStore.getStatsTablesAggregated(startDateDB, endDateDB, callback, true);

		},
		updateTables() {
			this.isLoading = true;

			let startDate = this.periodDates.previousStart;
			let endDate = this.periodDates.end;

			if(startDate) {
				let endDateDB = formatDateTimeWithSeconds(endDate);
				let otherStartDateDB = formatDateTimeWithSeconds(startDate);

				// For aggregated, use the same date range as orders (previousStart to end)
				// This ensures we get data for both current and previous periods in one call
				this.analyticsStore.getStatsTablesAggregated(otherStartDateDB, endDateDB, () => {
					this.isLoading = false;
				});

			} else {
				this.isLoading = false;
			}
		},
		updateAll() {
			this.isLoading = true;

			this.updateTables();
			this.updateTodayTables();

		},
		changedTimeframe(start, end, previousStart, previousEnd, timeframeIndex) {
			this.periodDates.start = start;
			this.periodDates.end = end;
			this.periodDates.previousStart = previousStart;
			this.periodDates.previousEnd = previousEnd;
			this.periodDates.timeframeIndex = timeframeIndex;
			this.updateTables();
		},
		scheduleNextRefresh() {
			// Clear any existing timeout
			if(this.realtimeTimeout) {
				clearTimeout(this.realtimeTimeout);
			}
			
			// Schedule the next refresh in 20 seconds
			this.realtimeTimeout = setTimeout(() => {
				this.updateTodayTables(() => {
					// Schedule the next refresh only after fetch completes
					this.scheduleNextRefresh();
				});
			}, 1000 * 20);
		}
	},
	mounted() {
		if (this.analyticsStore.loadedTables === false) {
			this.analyticsStore.loadedTables = true;
			this.periodDates.start = this.$refs["filters"].targetStartDate;
			this.periodDates.end = this.$refs["filters"].targetEndDate;
			this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
			this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
			this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
			this.updateAll();
		} else {
			// wait for the animation to finish
			setTimeout(() => {
				if(this.$refs["filters"]) {
					this.periodDates.start = this.$refs["filters"].targetStartDate;
					this.periodDates.end = this.$refs["filters"].targetEndDate;
					this.periodDates.previousStart = this.$refs["filters"].targetPreviousStartDate;
					this.periodDates.previousEnd = this.$refs["filters"].targetPreviousEndDate;
					this.periodDates.timeframeIndex = this.$refs["filters"].selectedTimeIndex;
				}
				this.updateTodayTables();
			}, 1000);
		}
		this.scheduleNextRefresh();

	},
	beforeUnmount() {
		if(this.realtimeTimeout) {
			clearTimeout(this.realtimeTimeout);
		}
	},
	watch: {
		"userStore.currBusinessId": function (newVal, oldVal) {
			if(newVal != null && newVal !== oldVal) {
				this.updateAll();
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.tables-analytics-container {
	max-width: 1400px;
	margin: 0 auto;

}
</style>